// The instruction the app sends on the user's behalf when they open the Map & review stage, plus the
// label the chat shows in its place.
//
// Pure and standalone, exactly like ./autoAnalysePrompt — it lives next to the panel rather than in
// `utils/index.js` (which Vitest cannot parse — see "Vitest imports from src/utils" in
// frontend/CLAUDE.md) and takes no hooks, so the wording can be asserted directly.
//
// Deliberately SHORT. The system prompt (app/agent/prompts/system.py) already spells out the mapping
// workflow, which tools to call in which order, and why the output frame must hold labels rather than
// ids. Restating any of that here would give the model two versions to reconcile, and the copy in this
// file is the one nobody would think to update. What this prompt adds is only what the situation changes:
//
//  1. The system prompt tells the agent to ask for confirmation before building the output (steps 4 and
//     6). Nobody asked for this turn, so there is nobody waiting to confirm — it has to go all the way.
//  2. A re-map must rebuild the frame the user is already looking at, not add a second one beside it.
//  3. The composer is LOCKED on this stage (see ./stages), so a question cannot be answered from where
//     the user is standing. The agent has to state what is blocking it and stop instead.

/** Frame keys, in a form safe to put in a sentence. Mirrors autoAnalysePrompt's helper. */
function frameList(frames) {
  const keys = (frames || [])
    .map((f) => (typeof f === "string" ? f : f?.key))
    .filter((k) => typeof k === "string" && k.length > 0);
  return Array.from(new Set(keys));
}

function quoted(keys) {
  return keys.map((k) => `"${k}"`).join(", ");
}

/**
 * @param {object}   args
 * @param {Array}    args.frames        The session's working frames, `{key}` objects or key strings.
 * @param {string}   args.target        AiIngestTarget name, when the session carries one.
 * @param {string}   args.outputFrame   Name of the output frame to rebuild, if one already exists.
 * @param {boolean}  args.isRemap       True when the user pressed "Re-map with AI" rather than arriving.
 * @returns {{text: string, display: string}} `text` goes to the model; `display` is shown instead.
 */
export function buildAutoMapPrompt({
  frames,
  target = "",
  outputFrame = "",
  isRemap = false,
} = {}) {
  const keys = frameList(frames);
  const named = keys.length > 0 ? quoted(keys) : "my working data";
  const destination = target ? `the ${target} data task` : "the destination data task";

  const lines = [
    isRemap
      ? `I have asked for this mapping to be rebuilt. Map ${named} onto ${destination}.`
      : `I have opened the Map & review stage. Map ${named} onto ${destination}.`,
    "",
    "Work straight through it without stopping to confirm anything with me:",
    "",
  ];

  if (target) {
    lines.push(`1. ${outputFrame ? "Re-read" : "Read"} the destination with get_task_template for ${target}.`);
  } else {
    lines.push("1. Read the destination with get_task_template.");
  }

  lines.push(
    "2. Work out the column and value mappings, using suggest_column_mappings and fuzzy_match_values.",
    outputFrame
      // Named, not "the existing output": the agent renames these as a session goes on, and a re-map that
      // guesses the name creates `mapped_output_2` beside the frame the user is reading.
      ? `3. Rebuild "${outputFrame}" in place — do NOT create a second output DataFrame beside it.`
      : "3. Build the mapped output DataFrame.",
    "4. Dry-run validate_against_task on it and fix what it reports, until it comes back clean.",
    "5. Summarise in a few lines what you mapped and anything you could not resolve.",
    "",
    // The guard rails. Both matter, and the second one is the whole reason this prompt is worded like
    // this rather than "please map my data".
    "Do NOT commit anything — I commit from the Commit stage myself.",
    "Do NOT ask me questions on this turn: I am on the review stage and the chat box is closed here. If",
    "something genuinely blocks you, say what it is and stop — I will go back to Preview & transform and",
    "sort it out with you there.",
  );

  return {
    display: isRemap
      ? `Re-mapping your data onto ${target || "the data task"}…`
      : `Mapping your data onto ${target || "the data task"}…`,
    text: lines.join("\n"),
  };
}

export default buildAutoMapPrompt;
